import _ from 'lodash';
import type Quill from 'quill/core';
import React, { useMemo } from 'react';
import useAppSelector from '../app/redux/useAppSelector';
import getUsersFromPresence from '../post/getUsersFromPresence';
import TargetHighlight from './TargetHighlight';
import Cursor from './Cursor';
import CursorSelection from './CursorSelection';
import { CREATION_ID } from './editorReducer';
import { useQuill } from './QuillContext';
import type Mark from './utils/Mark';

const isCollapsed = (mark: Mark) => {
  switch (mark.type) {
    case 'plain':
      return mark.length === 0;
    case 'cell':
      return mark.cell.length === 0;
    case 'code':
      return mark.code.length === 0;
    default:
      return true;
  }
};

interface MultiCursorProps {
  quill: Quill;
}

const MultiCursor = ({ quill }: MultiCursorProps) => {
  const presence = useAppSelector((state) => state.editor.presence);
  const remoteSelections = useAppSelector(
    (state) => state.editor.remoteSelections,
  );
  const isPickingReaction = useAppSelector(
    (state) => state.editor.pickingReaction,
  );
  const magneticWallFocus = useAppSelector(
    (state) => state.editor.magneticWallFocus,
  );

  const { readOnly } = useQuill();
  const users = useMemo(() => {
    const editors = getUsersFromPresence(Object.values(presence))
      .filter(({ status }) => status === 'editing')
      .map(({ user }) => user);
    return _.keyBy(editors, 'id');
  }, [presence]);

  return (
    <>
      {!readOnly &&
        _.map(remoteSelections, (mark, userId) => {
          const user = users[userId];
          if (user == null || mark == null) return false;
          return isCollapsed(mark) ? (
            <Cursor key={userId} quill={quill} user={user} mark={mark} />
          ) : (
            <CursorSelection
              key={userId}
              quill={quill}
              user={user}
              mark={mark}
            />
          );
        })}
      {(magneticWallFocus?.id === CREATION_ID || isPickingReaction) && (
        <TargetHighlight type={magneticWallFocus?.type ?? 'comment'} />
      )}
    </>
  );
};

export default MultiCursor;
